import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Helper function to format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Helper function to format time from 24-hour to 12-hour format
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper function to get semester label
const getSemesterLabel = (semester) => {
  if (semester === 1) return '1st Semester';
  if (semester === 2) return '2nd Semester';
  if (semester === 3) return 'Summer';
  return semester;
};

// Helper function to get year level label
const getYearLevelLabel = (level) => {
  const labels = { 1: '1st Year', 2: '2nd Year', 3: '3rd Year', 4: '4th Year' };
  return labels[level] || `Year ${level}`;
};

// Helper function to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
};

// Helper function to add header with logo
const addPDFHeader = async (doc, title) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  try {
    // Load and add logo
    const logoUrl = '/school-logo.jpg';
    const logoBase64 = await loadImageAsBase64(logoUrl);
    
    // Add logo (centered, size 20x20)
    const logoSize = 18;
    doc.addImage(logoBase64, 'JPEG', pageWidth / 2 - logoSize / 2, 8, logoSize, logoSize);
    
    // Header text below logo
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('OSMEÑA COLLEGES', pageWidth / 2, 32, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('College of Computer Science', pageWidth / 2, 38, { align: 'center' });
    doc.text('Masbate City, Philippines', pageWidth / 2, 43, { align: 'center' });
    
    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageWidth / 2, 52, { align: 'center' });
    
    return 58; // Return Y position after header
  } catch (error) {
    console.warn('Could not load logo, using text-only header:', error);
    
    // Fallback: text-only header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('OSMEÑA COLLEGES', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('College of Computer Science', pageWidth / 2, 27, { align: 'center' });
    doc.text('Masbate City, Philippines', pageWidth / 2, 33, { align: 'center' });
    
    // Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageWidth / 2, 45, { align: 'center' });
    
    return 50; // Return Y position after header
  }
};

// ==================== ADVANCED ANALYTICS PDF EXPORT ====================

/**
 * Export Advanced Analytics Report to PDF
 */
export const exportAdvancedAnalyticsToPDF = async (analyticsData) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  let yPos = await addPDFHeader(doc, 'Advanced Analytics Report');

  // Report date
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated: ${formatDate(new Date())}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;

  // Summary
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 14, yPos);
  yPos += 6;

  const summary = analyticsData.summary;
  const summaryRows = [
    ['Total Students', String(summary.totalStudents)],
    ['Students with Grades', String(summary.studentsWithGrades)],
    ['Average GWA', String(summary.averageGWA)],
    ['Best GWA', String(summary.highestGWA)],
    ['Lowest GWA', String(summary.lowestGWA)],
    ["Dean's List", String(summary.deansListCount)],
    ["President's List", String(summary.presidentsListCount)],
    ['Subjects Tracked', String(summary.totalSubjectsTracked)],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value']],
    body: summaryRows,
    theme: 'grid',
    headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { fontStyle: 'bold' } },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  // GWA Distribution
  if (yPos > 240) { doc.addPage(); yPos = 20; }
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('GWA Distribution', 14, yPos);
  yPos += 6;

  const gwaRows = Object.entries(analyticsData.gwaDistribution).map(([range, count]) => [range, String(count)]);
  autoTable(doc, {
    startY: yPos,
    head: [['GWA Range', 'Students']],
    body: gwaRows,
    theme: 'grid',
    headStyles: { fillColor: [139, 92, 246], fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  // Section Performance
  if (analyticsData.sectionPerformance.length > 0) {
    if (yPos > 220) { doc.addPage(); yPos = 20; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Section Performance Comparison', 14, yPos);
    yPos += 6;

    const sectionRows = analyticsData.sectionPerformance.map(s => [
      `Year ${s.yearLevel}`, `Section ${s.section}`, s.averageGWA, String(s.studentCount),
    ]);
    autoTable(doc, {
      startY: yPos,
      head: [['Year', 'Section', 'Avg GWA', 'Students']],
      body: sectionRows,
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Subject Pass/Fail Rates
  if (analyticsData.subjectPassFailRates.length > 0) {
    if (yPos > 200) { doc.addPage(); yPos = 20; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Subject Pass/Fail Rates', 14, yPos);
    yPos += 6;

    const subjectRows = analyticsData.subjectPassFailRates.map(s => [
      s.courseCode, s.subjectTitle, String(s.totalStudents),
      s.averageGrade, `${s.passRate}%`, String(s.failed),
    ]);
    autoTable(doc, {
      startY: yPos,
      head: [['Code', 'Subject', 'Students', 'Avg Grade', 'Pass Rate', 'Failed']],
      body: subjectRows,
      theme: 'grid',
      headStyles: { fillColor: [245, 158, 11], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Honors List
  if (analyticsData.honorsList.length > 0) {
    if (yPos > 200) { doc.addPage(); yPos = 20; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Honors List', 14, yPos);
    yPos += 6;

    const honorsRows = analyticsData.honorsList.map((s, i) => [
      String(i + 1), s.name, s.studentId, s.honor, s.gwa.toFixed(2),
    ]);
    autoTable(doc, {
      startY: yPos,
      head: [['#', 'Student', 'ID', 'Honor', 'GWA']],
      body: honorsRows,
      theme: 'grid',
      headStyles: { fillColor: [217, 119, 6], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // GWA Ranking
  if (analyticsData.gwaList.length > 0) {
    doc.addPage();
    yPos = 20;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Complete GWA Ranking', 14, yPos);
    yPos += 6;

    const rankingRows = analyticsData.gwaList.map((s, i) => [
      String(i + 1), s.name, s.studentId, `Year ${s.yearLevel}`, s.section, s.gwa.toFixed(2), String(s.totalUnits),
    ]);
    autoTable(doc, {
      startY: yPos,
      head: [['Rank', 'Student', 'ID', 'Year', 'Section', 'GWA', 'Units']],
      body: rankingRows,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });
  }

  // Footer on each page
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Advanced Analytics Report - Page ${i} of ${totalPages}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  doc.save(`Advanced_Analytics_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== PDF EXPORTS ====================

/**
 * Export Academic Record to PDF
 */
export const exportAcademicRecordToPDF = async (studentInfo, academicRecord) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header with logo
  const headerEndY = await addPDFHeader(doc, 'ACADEMIC RECORD');
  
  // Student Info
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const studentName = studentInfo.fullName || `${studentInfo.firstName} ${studentInfo.middleName ? studentInfo.middleName + ' ' : ''}${studentInfo.lastName}${studentInfo.suffix ? ' ' + studentInfo.suffix : ''}`;
  
  let infoY = headerEndY + 5;
  doc.text(`Student Name: ${studentName}`, 14, infoY);
  doc.text(`Student ID: ${studentInfo.studentId}`, 14, infoY + 6);
  doc.text(`Program: ${studentInfo.program || 'Bachelor of Science in Computer Science'}`, 14, infoY + 12);
  doc.text(`Year Level: ${getYearLevelLabel(studentInfo.yearLevel)}`, 14, infoY + 18);
  doc.text(`Date Generated: ${formatDate(new Date())}`, pageWidth - 14, infoY, { align: 'right' });
  
  // GWA
  if (academicRecord.gwa) {
    doc.setFont('helvetica', 'bold');
    doc.text(`General Weighted Average (GWA): ${academicRecord.gwa}`, pageWidth - 14, infoY + 6, { align: 'right' });
    doc.text(`Total Units: ${academicRecord.totalUnits}`, pageWidth - 14, infoY + 12, { align: 'right' });
    const remainingUnits = Math.max(0, 148 - (academicRecord.totalUnits || 0));
    doc.text(`Remaining Units: ${remainingUnits}`, pageWidth - 14, infoY + 18, { align: 'right' });
  }
  
  let yPosition = infoY + 30;
  
  // Grades by Semester
  if (academicRecord.grades && academicRecord.grades.length > 0) {
    academicRecord.grades.forEach((semesterData, index) => {
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Semester Header
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${getSemesterLabel(semesterData.semester)}, S.Y. ${semesterData.schoolYear} (${getYearLevelLabel(semesterData.yearLevel || semesterData.subjects[0]?.yearLevel)})`,
        14,
        yPosition
      );
      yPosition += 5;
      
      // Grades Table
      const tableData = semesterData.subjects.map(subject => {
        let grades = [];
        if (semesterData.semester === 3) {
          // Summer/OJT - only one grade
          grades = [subject.periods?.ojt?.grade ? parseFloat(subject.periods.ojt.grade).toFixed(2) : 'N/A'];
        } else {
          // Regular semester - 4 periods
          grades = [
            subject.periods?.prelim?.grade ? parseFloat(subject.periods.prelim.grade).toFixed(2) : 'N/A',
            subject.periods?.midterm?.grade ? parseFloat(subject.periods.midterm.grade).toFixed(2) : 'N/A',
            subject.periods?.prefinal?.grade ? parseFloat(subject.periods.prefinal.grade).toFixed(2) : 'N/A',
            subject.periods?.final?.grade ? parseFloat(subject.periods.final.grade).toFixed(2) : 'N/A',
          ];
        }
        
        return [
          subject.courseCode,
          subject.subjectTitle,
          subject.units,
          ...grades,
          subject.finalGrade || 'Inc.',
        ];
      });
      
      const headers = semesterData.semester === 3
        ? [['Course Code', 'Subject Title', 'Units', 'OJT Grade', 'Final Grade']]
        : [['Course Code', 'Subject Title', 'Units', 'Prelim', 'Midterm', 'Pre-Final', 'Final', 'Final Grade']];
      
      autoTable(doc, {
        startY: yPosition,
        head: headers,
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [8, 145, 178], textColor: 255, fontStyle: 'bold' },
        columnStyles: semesterData.semester === 3
          ? { 0: { cellWidth: 25 }, 1: { cellWidth: 70 }, 2: { cellWidth: 15 }, 3: { cellWidth: 25 }, 4: { cellWidth: 25 } }
          : { 0: { cellWidth: 22 }, 1: { cellWidth: 47 }, 2: { cellWidth: 12 }, 3: { cellWidth: 15 }, 4: { cellWidth: 15 }, 5: { cellWidth: 18 }, 6: { cellWidth: 15 }, 7: { cellWidth: 18 } },
        margin: { left: 14, right: 14 },
      });
      
      yPosition = doc.lastAutoTable.finalY + 10;
    });
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(
      `Page ${i} of ${pageCount} | Generated by CCS ChatBot Student Information System`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  // Save
  doc.save(`Academic_Record_${studentInfo.studentId}_${new Date().toISOString().split('T')[0]}.pdf`);
};

/**
 * Export Class Schedule to PDF
 */
export const exportScheduleToPDF = async (studentInfo, scheduleData) => {
  const doc = new jsPDF('landscape');
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header with logo
  let headerEndY = 50;
  try {
    const logoUrl = '/school-logo.jpg';
    const logoBase64 = await loadImageAsBase64(logoUrl);
    
    const logoSize = 16;
    doc.addImage(logoBase64, 'JPEG', pageWidth / 2 - logoSize / 2, 6, logoSize, logoSize);
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('OSMEÑA COLLEGES', pageWidth / 2, 27, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('College of Computer Science', pageWidth / 2, 33, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('CLASS SCHEDULE', pageWidth / 2, 42, { align: 'center' });
    headerEndY = 48;
  } catch (error) {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('OSMEÑA COLLEGES', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('College of Computer Science', pageWidth / 2, 22, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('CLASS SCHEDULE', pageWidth / 2, 32, { align: 'center' });
    headerEndY = 38;
  }
  
  // Student Info
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const studentName = studentInfo.fullName || `${studentInfo.firstName} ${studentInfo.lastName}`;
  doc.text(`Student: ${studentName}`, 14, headerEndY + 4);
  doc.text(`Student ID: ${studentInfo.studentId}`, 14, headerEndY + 10);
  doc.text(`Year Level: ${getYearLevelLabel(studentInfo.yearLevel)}`, 14, headerEndY + 16);
  doc.text(`Date Generated: ${formatDate(new Date())}`, pageWidth - 14, headerEndY + 4, { align: 'right' });
  
  // Convert schedule data to array if it's an object (grouped by day)
  let scheduleArray = [];
  if (scheduleData) {
    if (Array.isArray(scheduleData)) {
      scheduleArray = scheduleData;
    } else if (typeof scheduleData === 'object') {
      // It's an object with days as keys: { Monday: [...], Tuesday: [...] }
      Object.entries(scheduleData).forEach(([day, classes]) => {
        if (Array.isArray(classes)) {
          classes.forEach(classItem => {
            scheduleArray.push({
              ...classItem,
              scheduleDay: day,
              scheduleTimeStart: classItem.timeStart || classItem.scheduleTimeStart,
              scheduleTimeEnd: classItem.timeEnd || classItem.scheduleTimeEnd
            });
          });
        }
      });
    }
  }
  
  // Schedule Table
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const tableData = [];
  
  // Day pattern mapping (schedule patterns to individual days)
  const dayPatternMap = {
    'DAILY': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    'MWF': ['Monday', 'Wednesday', 'Friday'],
    'WF': ['Wednesday', 'Friday'],
    'TTH': ['Tuesday', 'Thursday'],
    'TTHS': ['Tuesday', 'Thursday', 'Saturday'],
    'MW': ['Monday', 'Wednesday'],
    'TH': ['Thursday'],
    'SAT': ['Saturday'],
    'SUN': ['Sunday'],
    'Monday': ['Monday'],
    'Tuesday': ['Tuesday'],
    'Wednesday': ['Wednesday'],
    'Thursday': ['Thursday'],
    'Friday': ['Friday'],
    'Saturday': ['Saturday'],
  };
  
  // Group schedule by day
  const scheduleByDay = {};
  days.forEach(day => scheduleByDay[day] = []);
  
  if (scheduleArray && scheduleArray.length > 0) {
    scheduleArray.forEach(item => {
      const itemDay = item.scheduleDay || item.day;
      if (itemDay) {
        // Get the individual days from the pattern
        const expandedDays = dayPatternMap[itemDay] || [itemDay];
        expandedDays.forEach(day => {
          if (scheduleByDay[day]) {
            scheduleByDay[day].push(item);
          }
        });
      }
    });
  }
  
  // Find max classes per day for row spanning
  const maxClasses = Math.max(...days.map(day => scheduleByDay[day].length || 1));
  
  for (let i = 0; i < maxClasses; i++) {
    const row = days.map(day => {
      const classes = scheduleByDay[day];
      if (classes[i]) {
        const c = classes[i];
        const timeStart = formatTimeTo12Hour(c.scheduleTimeStart || c.timeStart || '');
        const timeEnd = formatTimeTo12Hour(c.scheduleTimeEnd || c.timeEnd || '');
        return `${c.courseCode || ''}\n${timeStart} - ${timeEnd}\n${c.room || 'TBA'}${c.section ? `\nSec: ${c.section}` : ''}${c.classNumber ? `\nClass #${c.classNumber}` : ''}`;
      }
      return '';
    });
    tableData.push(row);
  }
  
  if (tableData.length === 0) {
    tableData.push(['No classes', '', '', '', '', '']);
  }
  
  autoTable(doc, {
    startY: headerEndY + 24,
    head: [days],
    body: tableData,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, valign: 'top', halign: 'center' },
    headStyles: { fillColor: [8, 145, 178], textColor: 255, fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 45 }, 2: { cellWidth: 45 }, 3: { cellWidth: 45 }, 4: { cellWidth: 45 }, 5: { cellWidth: 45 } },
    margin: { left: 14, right: 14 },
  });
  
  // Subject List
  let yPos = doc.lastAutoTable.finalY + 15;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('ENROLLED SUBJECTS', 14, yPos);
  yPos += 5;
  
  if (scheduleArray && scheduleArray.length > 0) {
    const subjectTableData = scheduleArray.map(s => [
      s.courseCode || '-',
      s.subjectTitle || '-',
      s.units || '-',
      s.section || '-',
      s.classNumber || '-',
      s.scheduleDay || s.day || '-',
      (s.scheduleTimeStart || s.timeStart) && (s.scheduleTimeEnd || s.timeEnd) 
        ? `${formatTimeTo12Hour(s.scheduleTimeStart || s.timeStart)} - ${formatTimeTo12Hour(s.scheduleTimeEnd || s.timeEnd)}` 
        : '-',
      s.room || 'TBA',
      (s.instructorFirstName && s.instructorLastName) 
        ? `${s.instructorFirstName} ${s.instructorLastName}` 
        : (s.instructor || '-')
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Course Code', 'Subjects with Descriptive Title', 'Units', 'Section', 'Class Number', 'Day', 'Time', 'Room', 'Instructor']],
      body: subjectTableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [8, 145, 178], textColor: 255, fontStyle: 'bold' },
      margin: { left: 14, right: 14 },
    });
  }
  
  // Footer
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text(
    `Generated by CCS ChatBot Student Information System | ${formatDate(new Date())}`,
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 10,
    { align: 'center' }
  );
  
  // Save
  doc.save(`Class_Schedule_${studentInfo.studentId}_${new Date().toISOString().split('T')[0]}.pdf`);
};

/**
 * Export Student Information to PDF — Professional Dark Maroon Branding
 */
export const exportStudentInfoToPDF = async (studentInfo) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Dark maroon brand colors
  const maroon = [100, 20, 30];
  const maroonLight = [140, 40, 50];
  const gold = [200, 170, 100];
  const cream = [252, 249, 244];
  const darkText = [50, 20, 25];
  
  // === TOP BANNER ===
  doc.setFillColor(...maroon);
  doc.rect(0, 0, pageWidth, 52, 'F');
  
  // Thin gold accent line
  doc.setFillColor(...gold);
  doc.rect(0, 52, pageWidth, 1.5, 'F');
  
  // Logo
  try {
    const logoUrl = '/school-logo.jpg';
    const logoBase64 = await loadImageAsBase64(logoUrl);
    doc.addImage(logoBase64, 'JPEG', pageWidth / 2 - 9, 4, 18, 18);
  } catch (e) { /* skip logo */ }
  
  // Header text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(17);
  doc.setFont('helvetica', 'bold');
  doc.text('OSMEÑA COLLEGES', pageWidth / 2, 28, { align: 'center' });
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gold);
  doc.text('College of Computer Science  •  Masbate City, Philippines', pageWidth / 2, 34, { align: 'center' });
  
  // Title bar
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('STUDENT INFORMATION', pageWidth / 2, 46, { align: 'center' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  const studentName = studentInfo.fullName || `${studentInfo.firstName} ${studentInfo.middleName ? studentInfo.middleName + ' ' : ''}${studentInfo.lastName}${studentInfo.suffix ? ' ' + studentInfo.suffix : ''}`;
  
  // === STUDENT NAME HIGHLIGHT ===
  doc.setFillColor(...cream);
  doc.roundedRect(14, 57, pageWidth - 28, 14, 2, 2, 'F');
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...maroon);
  doc.text(studentName, pageWidth / 2, 66, { align: 'center' });
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`Student ID: ${studentInfo.studentId || '-'}`, pageWidth / 2, 70, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  // === SECTION HELPER ===
  const drawSectionHeader = (label, y) => {
    doc.setFillColor(...maroon);
    doc.rect(14, y, pageWidth - 28, 8, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('  ' + label, 16, y + 5.5);
    doc.setTextColor(0, 0, 0);
    return y + 9;
  };

  // === PERSONAL INFORMATION ===
  let sectionY = drawSectionHeader('PERSONAL INFORMATION', 75);
  
  const personalData = [
    ['Student Type', studentInfo.studentType || '-'],
    ['Program', studentInfo.program || 'Bachelor of Science in Computer Science'],
    ['Year Level', getYearLevelLabel(studentInfo.yearLevel)],
    ['Enrollment Status', studentInfo.enrollmentStatus || '-'],
    ['Academic Status', studentInfo.academicStatus || '-'],
    ["Learner's Reference No.", studentInfo.learnerReferenceNumber || '-'],
    ['Email', studentInfo.email || '-'],
    ['Contact Number', studentInfo.contactNumber || '-'],
    ['Date of Birth', studentInfo.dateOfBirth ? (studentInfo.dateOfBirth.includes('/') ? studentInfo.dateOfBirth : formatDate(studentInfo.dateOfBirth)) : '-'],
    ['Birthplace', studentInfo.birthplace || '-'],
    ['Gender', studentInfo.gender || '-'],
    ['Civil Status', studentInfo.civilStatus || '-'],
    ['Masbate City Address', studentInfo.masbateCityAddress || '-'],
    ['Type of City Residence', studentInfo.cityResidenceType || '-'],
    ['Facebook Account', studentInfo.fbAccount || '-'],
    ['Enrollment Date', studentInfo.enrollmentDate ? formatDate(studentInfo.enrollmentDate) : '-'],
    ['Expected Graduation', studentInfo.expectedGraduationDate ? formatDate(studentInfo.expectedGraduationDate) : '-'],
    ['Guardian Name', studentInfo.guardianName || '-'],
    ['Guardian Contact', studentInfo.guardianContact || '-'],
    ['Guardian Address', studentInfo.guardianAddress || '-'],
  ];
  
  autoTable(doc, {
    startY: sectionY,
    body: personalData,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 6, right: 6 }, textColor: darkText, lineColor: [220, 210, 210], lineWidth: 0.2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: [...maroonLight] },
      1: { cellWidth: 125 }
    },
    alternateRowStyles: { fillColor: cream },
    margin: { left: 14, right: 14 },
    tableLineColor: [220, 210, 210],
    tableLineWidth: 0.2,
  });
  
  // === FAMILY BACKGROUND ===
  let yPos = doc.lastAutoTable.finalY + 6;
  if (yPos > pageHeight - 80) { doc.addPage(); yPos = 20; }
  sectionY = drawSectionHeader('FAMILY BACKGROUND', yPos);
  
  const familyData = [
    ["Father's Name", studentInfo.fatherName || '-'],
    ["Father's Occupation", studentInfo.fatherOccupation || '-'],
    ["Father's Contact", studentInfo.fatherContactNumber || '-'],
    ["Father's Facebook", studentInfo.fatherFbAccount || '-'],
    ["Father's Address", studentInfo.fatherAddress || '-'],
    ["Mother's Maiden Name", studentInfo.motherMaidenName || '-'],
    ["Mother's Occupation", studentInfo.motherOccupation || '-'],
    ["Mother's Contact", studentInfo.motherContactNumber || '-'],
    ["Mother's Facebook", studentInfo.motherFbAccount || '-'],
    ["Mother's Address", studentInfo.motherAddress || '-'],
    ['No. of Siblings', studentInfo.numberOfSiblings || '-'],
    ['Birth Order', studentInfo.birthOrder || '-'],
    ['Study Support', studentInfo.studySupportPerson || '-'],
    ['Relationship', studentInfo.studySupportRelationship || '-'],
    ['Support Contact', studentInfo.studySupportContact || '-'],
    ['Family Type', studentInfo.parentType || '-'],
    ['Monthly Family Income', studentInfo.monthlyFamilyIncome || '-'],
  ];
  
  autoTable(doc, {
    startY: sectionY,
    body: familyData,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 6, right: 6 }, textColor: darkText, lineColor: [220, 210, 210], lineWidth: 0.2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: [...maroonLight] },
      1: { cellWidth: 125 }
    },
    alternateRowStyles: { fillColor: cream },
    margin: { left: 14, right: 14 },
    tableLineColor: [220, 210, 210],
    tableLineWidth: 0.2,
  });

  // === EDUCATIONAL BACKGROUND ===
  yPos = doc.lastAutoTable.finalY + 6;
  if (yPos > pageHeight - 80) { doc.addPage(); yPos = 20; }
  sectionY = drawSectionHeader('EDUCATIONAL BACKGROUND', yPos);
  
  const educationData = [
    ['Tertiary School', studentInfo.tertiarySchool || '-'],
    ['Tertiary Address', studentInfo.tertiaryAddress || '-'],
    ['Tertiary Date Graduated', studentInfo.tertiaryDateGraduated || '-'],
    ['Tertiary Course', studentInfo.tertiaryCourse || '-'],
    ['Senior High School', studentInfo.seniorHighSchool || '-'],
    ['Senior High Address', studentInfo.seniorHighAddress || '-'],
    ['Senior High Date Graduated', studentInfo.seniorHighDateGraduated || '-'],
    ['Senior High Course/Strand', studentInfo.seniorHighCourse || '-'],
    ['Elementary School', studentInfo.elementarySchool || '-'],
    ['Elementary Address', studentInfo.elementaryAddress || '-'],
    ['Elementary Date Graduated', studentInfo.elementaryDateGraduated || '-'],
  ];
  
  autoTable(doc, {
    startY: sectionY,
    body: educationData,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 6, right: 6 }, textColor: darkText, lineColor: [220, 210, 210], lineWidth: 0.2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55, textColor: [...maroonLight] },
      1: { cellWidth: 125 }
    },
    alternateRowStyles: { fillColor: cream },
    margin: { left: 14, right: 14 },
    tableLineColor: [220, 210, 210],
    tableLineWidth: 0.2,
  });
  
  // === FOOTER on all pages ===
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    // Maroon footer bar
    doc.setFillColor(...maroon);
    doc.rect(0, pageHeight - 14, pageWidth, 14, 'F');
    doc.setFillColor(...gold);
    doc.rect(0, pageHeight - 14, pageWidth, 0.8, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(220, 200, 180);
    doc.text(
      `Generated by CCS ChatBot Student Information System  •  ${formatDate(new Date())}  •  Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 6,
      { align: 'center' }
    );
  }
  
  // Save
  doc.save(`Student_Info_${studentInfo.studentId}_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== EXCEL EXPORTS ====================

/**
 * Export Academic Record to Excel
 */
export const exportAcademicRecordToExcel = (studentInfo, academicRecord) => {
  const workbook = XLSX.utils.book_new();
  
  // Student Info Sheet
  const studentName = studentInfo.fullName || `${studentInfo.firstName} ${studentInfo.lastName}`;
  const infoData = [
    ['OSMEÑA COLLEGES - College of Computer Science'],
    ['ACADEMIC RECORD'],
    [],
    ['Student Name', studentName],
    ['Student ID', studentInfo.studentId],
    ['Program', studentInfo.program || 'Bachelor of Science in Computer Science'],
    ['Year Level', getYearLevelLabel(studentInfo.yearLevel)],
    ['GWA', academicRecord.gwa || 'N/A'],
    ['Total Units', academicRecord.totalUnits || 0],
    ['Remaining Units', Math.max(0, 148 - (academicRecord.totalUnits || 0))],
    ['Date Generated', formatDate(new Date())],
    [],
  ];
  
  // Grades by Semester
  if (academicRecord.grades && academicRecord.grades.length > 0) {
    academicRecord.grades.forEach((semesterData) => {
      infoData.push([]);
      infoData.push([`${getSemesterLabel(semesterData.semester)}, S.Y. ${semesterData.schoolYear}`]);
      
      if (semesterData.semester === 3) {
        infoData.push(['Course Code', 'Subject Title', 'Units', 'OJT Grade', 'Final Grade']);
      } else {
        infoData.push(['Course Code', 'Subject Title', 'Units', 'Prelim', 'Midterm', 'Pre-Final', 'Final', 'Final Grade']);
      }
      
      semesterData.subjects.forEach(subject => {
        if (semesterData.semester === 3) {
          infoData.push([
            subject.courseCode,
            subject.subjectTitle,
            subject.units,
            subject.periods?.ojt?.grade ? parseFloat(subject.periods.ojt.grade).toFixed(2) : 'N/A',
            subject.finalGrade || 'Inc.',
          ]);
        } else {
          infoData.push([
            subject.courseCode,
            subject.subjectTitle,
            subject.units,
            subject.periods?.prelim?.grade ? parseFloat(subject.periods.prelim.grade).toFixed(2) : 'N/A',
            subject.periods?.midterm?.grade ? parseFloat(subject.periods.midterm.grade).toFixed(2) : 'N/A',
            subject.periods?.prefinal?.grade ? parseFloat(subject.periods.prefinal.grade).toFixed(2) : 'N/A',
            subject.periods?.final?.grade ? parseFloat(subject.periods.final.grade).toFixed(2) : 'N/A',
            subject.finalGrade || 'Inc.',
          ]);
        }
      });
    });
  }
  
  const worksheet = XLSX.utils.aoa_to_sheet(infoData);
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 15 }, { wch: 40 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Academic Record');
  
  // Save
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(data, `Academic_Record_${studentInfo.studentId}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * Export Class Schedule to Excel
 */
export const exportScheduleToExcel = (studentInfo, scheduleData) => {
  const workbook = XLSX.utils.book_new();
  
  const studentName = studentInfo.fullName || `${studentInfo.firstName} ${studentInfo.lastName}`;
  
  // Convert schedule data to array if it's an object (grouped by day)
  let scheduleArray = [];
  if (scheduleData) {
    if (Array.isArray(scheduleData)) {
      scheduleArray = scheduleData;
    } else if (typeof scheduleData === 'object') {
      // It's an object with days as keys: { Monday: [...], Tuesday: [...] }
      Object.entries(scheduleData).forEach(([day, classes]) => {
        if (Array.isArray(classes)) {
          classes.forEach(classItem => {
            scheduleArray.push({
              ...classItem,
              scheduleDay: day,
              scheduleTimeStart: classItem.timeStart || classItem.scheduleTimeStart,
              scheduleTimeEnd: classItem.timeEnd || classItem.scheduleTimeEnd
            });
          });
        }
      });
    }
  }
  
  // Schedule Sheet
  const scheduleSheetData = [
    ['OSMEÑA COLLEGES - College of Computer Science'],
    ['CLASS SCHEDULE'],
    [],
    ['Student Name', studentName],
    ['Student ID', studentInfo.studentId],
    ['Year Level', getYearLevelLabel(studentInfo.yearLevel)],
    ['Date Generated', formatDate(new Date())],
    [],
    ['Course Code', 'Subject Title', 'Units', 'Section', 'Class #', 'Day', 'Time', 'Room', 'Instructor'],
  ];
  
  if (scheduleArray && scheduleArray.length > 0) {
    scheduleArray.forEach(s => {
      scheduleSheetData.push([
        s.courseCode || '-',
        s.subjectTitle || '-',
        s.units || '-',
        s.section || '-',
        s.classNumber || '-',
        s.scheduleDay || s.day || '-',
        (s.scheduleTimeStart || s.timeStart) && (s.scheduleTimeEnd || s.timeEnd) 
          ? `${formatTimeTo12Hour(s.scheduleTimeStart || s.timeStart)} - ${formatTimeTo12Hour(s.scheduleTimeEnd || s.timeEnd)}` 
          : '-',
        s.room || 'TBA',
        (s.instructorFirstName && s.instructorLastName) 
          ? `${s.instructorFirstName} ${s.instructorLastName}` 
          : (s.instructor || '-')
      ]);
    });
  }
  
  const worksheet = XLSX.utils.aoa_to_sheet(scheduleSheetData);
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 15 }, { wch: 35 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 25 }
  ];
  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Class Schedule');
  
  // Save
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(data, `Class_Schedule_${studentInfo.studentId}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// Default export
const exportUtils = {
  exportAcademicRecordToPDF,
  exportScheduleToPDF,
  exportStudentInfoToPDF,
  exportAcademicRecordToExcel,
  exportScheduleToExcel,
  exportAdvancedAnalyticsToPDF,
};

export default exportUtils;
